import { useFirebase } from "containers/FirebaseContext";
import { collection, doc as firestoreDoc, getDoc, getDocs, onSnapshot, orderBy, query, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import moment from "moment";
import { get, filter } from "lodash";
import { getUserAssignment } from "utils/lib/ebookHelper";
import { useClassSubmissions } from "store/submissions/selectors";
import { v4 } from 'uuid';
import * as Sentry from '@sentry/react';
import useActiveStudentofParent from "components/hooks/parent/activeStudentofParent";
import { useCallback } from "react";

const filterWeekAssignmentPast = (assignment) => {
    if (!assignment) return false;
    const _dueDate = get(assignment, "lateDate")
    return moment(_dueDate).isSame(moment(), "week")
}

const filterWeekAssignment = (assignment) => {
    if (!assignment) return false;
    const _dueDate = get(assignment, "dueDate")
    return moment(_dueDate).isSame(moment(), "week")
}

const getStudentSubmission = (weekAssignmentPast, type, submissions) => {
    const filteredData = weekAssignmentPast
        .filter(answer => {
            const assignment = submissions?.find(item => item.assignmentId === answer.data?.id);
            const diff = get(assignment, "diff[0]");
            let retakeAvailable = false;

            if (diff) {
                retakeAvailable = assignment ? assignment?.exceptionStatus ==3?true: parseInt(get(assignment, "submissions", 0), 10) < (parseInt(get(diff, "retakes"), 10) + 1) : true;
            } else {
                retakeAvailable = assignment ? assignment?.exceptionStatus ==3?true: parseInt(get(assignment, "submissions", 0), 10) < (parseInt(get(answer, "retakes"), 10) + 1) : true;
            }
            const completed = !retakeAvailable && get(assignment, "complete", false);
            const retake = retakeAvailable && !completed;
            return retake;
        })
        
    const getMissingFilter = filter(filteredData, a => a.lateDate && moment(a.dueDate).isBefore(moment(), "minute") && moment(a.lateDate).isAfter(moment(), "minute"));
    const getDueFilter = filter(filteredData, a => moment(a.dueDate).isSame(moment(), "day") && moment(a.dueDate).isAfter(moment(), "minute") && !moment(a.scheduleAt).isSame(moment(), "day"))
    if (type === 'missing') {
        return getMissingFilter;
    } else if (type === 'due'){
        return getDueFilter;
    }
};

export const useCollectionNotification = () => {
    const { firestore , firebaseUser } = useFirebase();
    const submissions = useClassSubmissions();
    const { activeStudentParent } = useActiveStudentofParent();
    const createCollectionNotification = async (notificationData, classId) => {
        try {
            if (!firebaseUser) {
                return;
            }
            const currentTime = new Date().toISOString();
            const generated_session_id = classId + currentTime;
            const docRef = firestoreDoc(firestore, "notifications", generated_session_id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                await updateDoc(docRef, notificationData)
            } else {
                await setDoc(docRef, notificationData)
            }
            return;
        } catch (error) {
            Sentry.captureException(`652 Error in createCollectionNotification: ${error}`);
            console.log(error, "error")
        }
    }
    const createNotificationAssignment = async (notificationData, usersByClass) => {
        try {
            if (!firebaseUser) {
                return;
            }
            const currentTime = new Date().toISOString();
            const allClasses = notificationData?.data?.classes ?? [];
            // Loop through each class and send separate notifications
            for (const cls of allClasses) {
                const classUser = [];
                classUser.push(...get(usersByClass, cls.id, []));
                let studentsData = classUser.filter(x => x.role === 'student').map(user => user.id);
                if (cls.users?.length) studentsData = studentsData.filter(id => cls.users.includes(id));
                const generated_session_id = cls.id + currentTime; // Unique ID per class
                const docRef = firestoreDoc(firestore, "notifications", generated_session_id);
                const docSnap = await getDoc(docRef);
                notificationData.classId = cls.id;
                notificationData.receiversId = studentsData;
                if (docSnap.exists()) {
                    await updateDoc(docRef, notificationData);
                } else {
                    await setDoc(docRef, notificationData);
                }
            }
        } catch (error) {
            Sentry.captureException(`653 Error in createNotificationAssignment: ${error}`);
            console.log(error, "error");
        }
    };

    const createBatchNotificationAssignment = async (notificationsArray, usersByClass) => {
        try {
            if (!firebaseUser || !notificationsArray || notificationsArray.length === 0) {
                return;
            }
            const currentTime = new Date().toISOString();
            const docRefsToCheck = [];

            notificationsArray.forEach((notificationData) => {
                const allClasses = notificationData?.data?.classes ?? [];
                allClasses.forEach((cls) => {
                    const classUser = [];
                    classUser.push(...get(usersByClass, cls.id, []));
                    let studentsData = classUser.filter(x => x.role === 'student').map(user => user.id);
                    if (cls.users?.length) studentsData = studentsData.filter(id => cls.users.includes(id));
                    const generated_session_id = `${cls.id}_${notificationData.id}_${currentTime}`;
                    const docRef = firestoreDoc(firestore, "notifications", generated_session_id);
                    
                    const notificationPayload = {
                        ...notificationData,
                        classId: cls.id,
                        receiversId: studentsData,
                    };

                    docRefsToCheck.push({ docRef, payload: notificationPayload });
                });
            });

            if (docRefsToCheck.length === 0) {
                return;
            }

            const checkPromises = docRefsToCheck.map(({ docRef }) => getDoc(docRef));
            const docSnaps = await Promise.all(checkPromises);

            const BATCH_LIMIT = 500;
            const batches = [];

            for (let i = 0; i < docRefsToCheck.length; i += BATCH_LIMIT) {
                const batch = writeBatch(firestore);
                const chunk = docRefsToCheck.slice(i, i + BATCH_LIMIT);
                const chunkSnaps = docSnaps.slice(i, i + BATCH_LIMIT);

                chunkSnaps.forEach((docSnap, index) => {
                    const { docRef, payload } = chunk[index];
                    if (docSnap.exists()) {
                        batch.update(docRef, payload);
                    } else {
                        batch.set(docRef, payload);
                    }
                });

                batches.push(batch.commit());
            }

            await Promise.all(batches);
        } catch (error) {
            Sentry.captureException(`657 Error in createBatchNotificationAssignment: ${error}`);
            console.log(error, "error in batch notification assignment");
        }
    };
    const updateCollectionNotification = useCallback(async (id, userId) => {
        try {
            if (!id || !userId || !firebaseUser) {
                return;
            }
            const notificationsRef = collection(firestore, "notifications");
            const q = query(notificationsRef, where("id", "==", id));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach(async (doc) => {
                const data = doc.data();
                const existingUser = data.userSeen && data.userSeen.includes(userId);
                if (!existingUser) {
                    const docRef = firestoreDoc(notificationsRef, doc.id);
                    await updateDoc(docRef, {
                        userSeen: [...(data.userSeen || []), userId]
                    });
                }
            })
        } catch (error) {
            Sentry.captureException(`654 Error in updateCollectionNotification: ${error}`);
            console.log(error, "error update notification")
        }
    }, [firebaseUser,firestore]);

    const updateNotificationsFetched = useCallback(async (notificationDocIds = []) => {
        try {
            if (!firebaseUser || !Array.isArray(notificationDocIds) || notificationDocIds.length === 0) {
                return;
            }
            const uniqueDocIds = [...new Set(notificationDocIds.filter(Boolean))];
            if (uniqueDocIds.length === 0) {
                return;
            }

            const BATCH_LIMIT = 500;
            const batchCommits = [];

            for (let i = 0; i < uniqueDocIds.length; i += BATCH_LIMIT) {
                const batch = writeBatch(firestore);
                uniqueDocIds.slice(i, i + BATCH_LIMIT).forEach((docId) => {
                    batch.update(firestoreDoc(firestore, "notifications", docId), {
                        is_fetched: true,
                    });
                });
                batchCommits.push(batch.commit());
            }

            await Promise.allSettled(batchCommits);
        } catch (error) {
            Sentry.captureException(`658 Error in updateNotificationsFetched: ${error}`);
            console.log(error, "error update notification fetched");
        }
    }, [firebaseUser, firestore]);

    const updateNotificationByInstructionId = useCallback(async (instructionId, classId, userId) => {
        try {
            if (!instructionId || !classId || !userId || !firebaseUser) {
                return;
            }
            const notificationsRef = collection(firestore, "notifications");
            const q = query(
                notificationsRef, 
                where("data.id", "==", instructionId),
                where("classId", "==", classId),
                where("receiversId", "array-contains", userId)
            );
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach(async (doc) => {
                const data = doc.data();
                const existingUser = data.userSeen && data.userSeen.includes(userId);
                if (!existingUser) {
                    const docRef = firestoreDoc(notificationsRef, doc.id);
                    await updateDoc(docRef, {
                        userSeen: [...(data.userSeen || []), userId]
                    });
                }
            });
        } catch (error) {
            Sentry.captureException(`656 Error in updateNotificationByInstructionId: ${error}`);
            console.log(error, "error update notification by instructionId");
        }
    }, [firebaseUser, firestore]);

    const markAllAsRead = async (userId, classId, onProgress) => {
        try {
            if (!firebaseUser) {
                return;
            }
            const notificationsRef = collection(firestore, "notifications");
            const q = query(notificationsRef, where("classId", "==", classId));
            const querySnapshot = await getDocs(q);
            
            const docsToUpdate = querySnapshot.docs.filter((doc) => {
                const data = doc.data();
                return !data.userSeen?.includes(userId);
            });

            if (docsToUpdate.length === 0) {
                return;
            }

            const BATCH_LIMIT = 500;
            const batches = [];
            const totalBatches = Math.ceil(docsToUpdate.length / BATCH_LIMIT);

            for (let i = 0; i < docsToUpdate.length; i += BATCH_LIMIT) {
                const batch = writeBatch(firestore);
                const chunk = docsToUpdate.slice(i, i + BATCH_LIMIT);
                const currentBatch = Math.floor(i / BATCH_LIMIT) + 1;

                chunk.forEach((doc) => {
                    const data = doc.data();
                    const docRef = firestoreDoc(firestore, "notifications", doc.id);
                    batch.update(docRef, {
                        userSeen: [...(data.userSeen || []), userId]
                    });
                });

                batches.push(batch.commit());
                
                if (onProgress) {
                    onProgress({
                        current: currentBatch,
                        total: totalBatches,
                        processed: Math.min(i + BATCH_LIMIT, docsToUpdate.length),
                        totalDocs: docsToUpdate.length
                    });
                }
            }

            await Promise.all(batches);
        } catch (error) {
            Sentry.captureException(`655 Error in markAllAsRead: ${error}`);
            console.error("Error marking all notifications as read:", error);
            throw error;
        }
    };

    const deleteOldNotifications = async () => {
        // try {
        //     const notificationsRef = collection(firestore, "notifications");
        //     const thresholdDate = moment().subtract(3, "months").toISOString();
        //     const q = query(notificationsRef, where("modified", "<", thresholdDate));
        //     const querySnapshot = await getDocs(q);
        //     if (querySnapshot.empty) {
        //         return;
        //     }
        //     const batch = writeBatch(firestore);

        //     querySnapshot.forEach(async (doc) => {
        //         const docRef = doc.ref;
        //         batch.delete(docRef);
        //     });
        //     await batch.commit();
        // } catch (error) {
        //     console.error("Error deleting old notifications:", error);
        // }
    };

    const createCollectionMissingNotification = async (notificationsData) => {
        try {
            if (!firebaseUser) {
                return;
            }
            const batch = writeBatch(firestore);
            const existingAssignmentIds = new Set();
            const collectionRef = collection(firestore, "notifications");
            const assignmentIdsToCheck = notificationsData.map((data) => data.assignmentId?.trim());

            if (assignmentIdsToCheck.length === 0) {
                console.warn("No assignment IDs provided.");
                return;
            }

            const limitedIds = assignmentIdsToCheck.slice(0, 10);
            const q = query(collectionRef, where("assignmentId", "in", limitedIds));
            const querySnapshot = await getDocs(q);

            querySnapshot.forEach((doc) => {
                const existingAssignmentId = doc.data().assignmentId;
                existingAssignmentIds.add(existingAssignmentId);
            });
            notificationsData.forEach((notificationData) => {
                const assignmentId = notificationData.assignmentId.trim();
    
                if (!existingAssignmentIds.has(assignmentId)) {
                    const docRef = firestoreDoc(firestore, "notifications", notificationData.id);
                    batch.set(docRef, notificationData);
                    existingAssignmentIds.add(assignmentId);
                }
            });

            await batch.commit();
        } catch (error) {
            Sentry.captureException(`656 Error in createCollectionMissingNotification: ${error}`);
            console.error("Error in batch notification creation:", error);
        }
    };

    const onMissingNotification = async (currentClass, booksAllList, allAssignment, currentUser, bookPermissions,allowed_books) => {
        if (!firebaseUser) {
            return;
        }
        const userAssignment = getUserAssignment(allowed_books , allAssignment, currentUser, currentClass,null,activeStudentParent);
        const weekAssignmentPast = filter(userAssignment, filterWeekAssignmentPast);
        const weekAssignment = filter(userAssignment, filterWeekAssignment)
        const pastThisWeek = getStudentSubmission(weekAssignmentPast, 'missing', submissions);
        const todayDueAssignment = getStudentSubmission(weekAssignment, 'due');
        const pastNotifications = pastThisWeek.map((obj) => ({
            id: v4(),
            classId: get(currentClass, "id", ""),
            displayName: get(obj, "displayName", ""),
            senderId: get(currentClass, "teacherId", ""),
            receiversId: [].concat(get(currentUser, "id", "")),
            modified: moment().toISOString(),
            data: {
                chapter: get(obj, "data.chapter", ""),
                page: get(obj, "data.page", ""),
                id: get(obj, "data.id", ""),
            },
            assignmentId: get(obj, "data.id", "") + get(currentUser, "id", ""),
            message: "You have this missing assignment",
            type: "missing_assignment",
            userSeen: [],
        }));
        const dueNotifications = todayDueAssignment.map((obj) => ({
            id: v4(),
            classId: get(currentClass, "id", ""),
            displayName: get(obj, "displayName", ""),
            senderId: get(currentClass, "teacherId", ""),
            receiversId: [].concat(get(currentUser, "id", "")),
            modified: moment().toISOString(),
            data: {
                chapter: get(obj, "data.chapter", ""),
                page: get(obj, "data.page", ""),
                id: get(obj, "data.id", ""),
            },
            assignmentId: get(obj, "data.id", "") + get(currentUser, "id", ""),
            message: "You have this due assignment",
            type: "due_assignment",
            userSeen: [],
        }));
    
        const notificationsData = [...pastNotifications, ...dueNotifications];
        await createCollectionMissingNotification(notificationsData);
    }

    const listenToNotificationData = (classID,currentUser, callBack) => {
      if (!classID || !currentUser || !firebaseUser) {
        return () => {};
      }
      const collectionRef = collection(firestore, 'notifications');
      const q = query(
        collectionRef,
        where('classId', '==', classID),
        where('receiversId', 'array-contains', currentUser.id),
        where('senderId', '!=', currentUser.id),
        orderBy('modified', 'desc'),
      );
      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const notifications = [];
          querySnapshot.forEach((doc) => {
            notifications.push({ id: doc.id, ...doc.data() });
          });
          const filterBySender = filter(notifications, (notification) =>
            notification.receiversId?.includes(currentUser.id),
          );
          callBack([...filterBySender]);
        },
        (error) => {
          if (error.code === 'permission-denied') {
            console.warn("Permission denied for notifications collection. User may not be properly authenticated.");
            return;
          }
          console.error('Error listening to session changes:', error);
        },
      );
      return unsubscribe;
    };

    return { createCollectionNotification,listenToNotificationData, updateCollectionNotification, updateNotificationByInstructionId, updateNotificationsFetched, markAllAsRead, deleteOldNotifications, onMissingNotification, createNotificationAssignment, createBatchNotificationAssignment }
}